'use client'

import { useEffect } from 'react'
import { useUserProfile } from '@/hooks/use-user-profile'

/**
 * Global fetch interceptor that automatically measures Client / Network / Server
 * timing for every /api/* call. Drop into the root layout — no per-component
 * changes needed.
 *
 * Writes metrics via keepalive POST to /api/performance/metrics.
 * Skips tracking its own metrics call to avoid infinite loops.
 */
export function FetchInterceptor() {
  const { profile } = useUserProfile()
  const userEmail = profile?.email ?? null

  useEffect(() => {
    // Only run in the browser
    if (typeof window === 'undefined') return

    const originalFetch = window.fetch

    // Guard against double-patching (e.g. HMR)
    if ((originalFetch as unknown as { __perf_patched?: boolean }).__perf_patched) return

    const patchedFetch: typeof window.fetch = async (input, init) => {
      // Resolve URL string
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input instanceof Request
              ? input.url
              : String(input)

      // Only track our own API calls, skip the metrics endpoint itself
      const isApiCall = url.includes('/api/')
      const isMetricsCall = url.includes('/api/performance/metrics')

      if (!isApiCall || isMetricsCall) {
        return originalFetch(input, init)
      }

      const startTime = performance.now()
      const method = init?.method || (typeof input === 'object' && 'method' in input ? input.method : 'GET') || 'GET'

      // All metrics work happens HERE, detached from the caller's promise.
      // The previous version did this inside a `finally` block with awaits in
      // it (a setTimeout tick + reading a cloned response body), which meant
      // the caller's own `await fetch(...)` could not resolve until the
      // metrics bookkeeping finished — and if the cloned body ever stalled,
      // every API call in the app hung silently. Metrics must observe the
      // response, never gate it.
      const recordMetric = async (opts: {
        fetchError: Error | null
        response: Response | null
        errorClone: Response | null
      }) => {
        try {
          const { fetchError, response, errorClone } = opts
          const totalDuration = Math.round(performance.now() - startTime)

          // Parse server duration from Server-Timing header
          let serverDuration = 0
          if (response) {
            const serverTimingHeader = response.headers.get('Server-Timing')
            if (serverTimingHeader) {
              const match = serverTimingHeader.match(/server;dur=(\d+(?:\.\d+)?)/)
              if (match) serverDuration = Math.round(parseFloat(match[1]))
            }
          }

          // Use PerformanceResourceTiming for network breakdown
          let networkRoundTrip = 0
          if (window.performance) {
            // Give browser a tick to finalize resource timing
            await new Promise(r => setTimeout(r, 0))
            const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
            const resolvedUrl = new URL(url, window.location.origin).href
            const entry = entries
              .filter(e => e.name === resolvedUrl || e.name.startsWith(resolvedUrl + '?'))
              .pop()
            if (entry && entry.requestStart > 0) {
              networkRoundTrip = Math.round(entry.responseEnd - entry.requestStart)
            }
          }

          const networkDuration = Math.max(0, networkRoundTrip - serverDuration)
          const clientDuration = Math.max(0, totalDuration - networkRoundTrip)

          // Try to extract error message from failed responses
          let errorMessage: string | null = null
          if (fetchError) {
            errorMessage = fetchError.message
          } else if (errorClone) {
            try {
              const body = await errorClone.json()
              errorMessage = body.error || body.message || body.detail || null
              if (typeof errorMessage === 'object') errorMessage = JSON.stringify(errorMessage)
            } catch {
              // Body isn't JSON — that's fine
            }
          }

          // Fire-and-forget metric write
          await originalFetch('/api/performance/metrics', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({
              transaction_name: deriveRouteName(url, method),
              transaction_type: 'api_call',
              url,
              method,
              total_duration: totalDuration,
              client_duration: clientDuration,
              network_duration: networkDuration,
              server_duration: serverDuration,
              status_code: fetchError ? 0 : response!.status,
              success: fetchError ? false : response!.ok,
              error_message: errorMessage ? String(errorMessage).slice(0, 500) : null,
              user_email: userEmail,
              environment: process.env.NODE_ENV || 'production',
            }),
          })
        } catch {
          // Silent — never break the app for metrics
        }
      }

      let response: Response
      try {
        response = await originalFetch(input, init)
      } catch (err) {
        const fetchError = err instanceof Error ? err : new Error(String(err))
        void recordMetric({ fetchError, response: null, errorClone: null })
        // Re-throw so calling code still sees the error
        throw fetchError
      }

      // Clone SYNCHRONOUSLY (before returning) so the caller consuming the
      // body can't race us; the detached task always drains the clone, so the
      // tee'd stream never backs up. Only failed responses need a body read.
      let errorClone: Response | null = null
      if (!response.ok) {
        try { errorClone = response.clone() } catch { /* body already locked */ }
      }
      void recordMetric({ fetchError: null, response, errorClone })

      return response
    }

    // Mark as patched to prevent double-patching on HMR
    ;(patchedFetch as unknown as { __perf_patched: boolean }).__perf_patched = true
    window.fetch = patchedFetch

    return () => {
      // Restore original fetch on cleanup
      window.fetch = originalFetch
    }
  }, [userEmail])

  return null
}

/**
 * Derive a human-readable route name from a URL.
 * /api/flight/reservations/abc-123 → flight_reservations_[id]
 */
function deriveRouteName(url: string, method?: string): string {
  try {
    const path = new URL(url, window.location.origin).pathname
    const segments = path
      .replace(/^\/api\//, '')
      .split('/')
      .filter(Boolean)
      .map(seg => {
        if (/^[0-9a-f-]{8,}$/i.test(seg) || /^\d+$/.test(seg)) return '[id]'
        return seg
      })
    const prefix = (method || 'GET').toLowerCase()
    return `${segments.join('_')}_${prefix}`
  } catch {
    return `unknown_${(method || 'GET').toLowerCase()}`
  }
}
